/**
 * Old/new UI switch. The Dashboard API serves the legacy UI (this app) or the new Blazor UI from the same address,
 * depending on a cookie that `/ui/switch` sets before redirecting to `returnUrl`.
 */

// Pages that exist in both UIs, with their address in the new UI. Everything else opens the new UI's home page.
const equivalentModernPaths: Record<string, string> = {
    "/": "/",
    "/my-preferences": "/me/preferences",
    "/user-management/users": "/user-management/users",
    "/user-management/groups": "/user-management/groups",
};

/** Address of the page that matches `pathname` in the new UI, or "/" when there is no equivalent page (or no path yet). */
export const getModernPath = (pathname: string | null | undefined): string => {
    if (!pathname) return "/";

    const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    return Object.prototype.hasOwnProperty.call(equivalentModernPaths, path) ? equivalentModernPaths[path] : "/";
};

/**
 * What the API says about the switch. `modernUrl` is set in development only, where the new UI runs on its own dev server
 * (Aspire full stack); in production both UIs share one origin and the switch goes through `/ui/switch`.
 */
export interface IUiSwitchInfo {
    modern: boolean;
    modernUrl: string | null;
}

const noSwitch: IUiSwitchInfo = { modern: false, modernUrl: null };
let uiSwitchInfo: Promise<IUiSwitchInfo> | null = null;

/** Whether, and where, the new UI can be offered; asked once per page load. */
export const getUiSwitchInfo = (): Promise<IUiSwitchInfo> => {
    uiSwitchInfo ??= fetch("/ui/available", { credentials: "same-origin" })
        .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.json();
        })
        .then((body: { modern?: boolean; modernUrl?: string | null; }): IUiSwitchInfo => ({ modern: body.modern === true, modernUrl: body.modernUrl || null }))
        .catch(() => {
            uiSwitchInfo = null; // a transient failure must not hide the entry until the next page load
            return noSwitch;
        });
    return uiSwitchInfo;
};

/** Opens the new UI with a full page load. Both UIs share one sign-in (same MSAL cache in localStorage), so the new one opens signed in. */
export const switchToModern = (info: IUiSwitchInfo, pathname: string | null | undefined): void => {
    window.location.assign(getSwitchToModernUrl(pathname, info));
};

/** Where the "try the new version" link goes: the new UI's dev server, or `/ui/switch` which selects it and opens the page. */
export const getSwitchToModernUrl = (pathname: string | null | undefined, info: IUiSwitchInfo): string => (
    info.modernUrl
        ? `${info.modernUrl.replace(/\/+$/, "")}${getModernPath(pathname)}`
        : `/ui/switch?to=modern&returnUrl=${encodeURIComponent(getModernPath(pathname))}`
);
